import { useMemo, useState } from "react";
import {
  COUNTRY_GROUPS,
  countryName,
  filterCountries,
  parseBulkCountryCodes,
  popularCountries,
} from "../lib/countries";
import { fieldValue } from "../lib/polaris";

type Props = {
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  error?: string | null;
  label?: string;
  /** ISO → reason already used elsewhere, e.g. "Used in Block". */
  usedElsewhere?: Record<string, string>;
};

/**
 * Searchable multi-select - country **names** first (Horizon / Markets UX).
 * Codes stay internal; advanced paste still accepts ISO.
 */
export function CountryMultiSelect({
  value,
  onChange,
  disabled,
  error,
  label = "Countries",
  usedElsewhere = {},
}: Props) {
  const [query, setQuery] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bulk, setBulk] = useState("");

  const selected = useMemo(
    () => value.map((c) => c.toUpperCase()).filter(Boolean),
    [value],
  );

  const popular = useMemo(
    () => popularCountries(selected).slice(0, 8),
    [selected],
  );

  const matches = useMemo(
    () => filterCountries(query, selected).slice(0, 40),
    [query, selected],
  );

  function add(code: string) {
    const c = code.toUpperCase();
    if (selected.includes(c)) return;
    if (usedElsewhere[c]) return;
    onChange([...selected, c]);
    setQuery("");
  }

  function optionLabel(c: { code: string; name: string }) {
    const used = usedElsewhere[c.code];
    return used ? `${c.name} - ${used}` : c.name;
  }

  function remove(code: string) {
    onChange(selected.filter((c) => c !== code));
  }

  function applyBulk() {
    const parsed = parseBulkCountryCodes(bulk);
    if (!parsed.length) return;
    const merged = [...selected];
    for (const c of parsed) {
      if (!merged.includes(c) && !usedElsewhere[c]) merged.push(c);
    }
    onChange(merged);
    setBulk("");
    setShowAdvanced(false);
  }

  function addGroup(codes: string[]) {
    const merged = [...selected];
    for (const raw of codes) {
      const c = raw.toUpperCase();
      if (!merged.includes(c) && !usedElsewhere[c]) merged.push(c);
    }
    onChange(merged);
  }

  const chips =
    selected.length <= 12
      ? selected
      : selected.slice(0, 12);

  return (
    <s-stack direction="block" gap="base">
      <s-text-field
        label={label}
        value={query}
        disabled={disabled}
        error={error || undefined}
        onChange={(e: Event) => setQuery(fieldValue(e))}
        details="Search by country name (e.g. United Kingdom). We save the country for routing."
        autocomplete="off"
      />

      {selected.length > 0 ? (
        <s-stack gap="small">
          <s-stack direction="inline" gap="small">
            {chips.map((code) => (
              <s-button
                key={code}
                type="button"
                variant="secondary"
                disabled={disabled}
                onClick={() => remove(code)}
                accessibilityLabel={`Remove ${countryName(code)}`}
              >
                {countryName(code)} ✕
              </s-button>
            ))}
            {selected.length > 12 ? (
              <s-text color="subdued">
                and {selected.length - 12} more
              </s-text>
            ) : null}
          </s-stack>
          <s-button
            type="button"
            variant="tertiary"
            disabled={disabled}
            onClick={() => onChange([])}
          >
            Clear all
          </s-button>
        </s-stack>
      ) : (
        <s-paragraph color="subdued">No countries selected yet.</s-paragraph>
      )}

      <s-stack gap="small">
        <s-text color="subdued">Add a group:</s-text>
        <s-stack direction="inline" gap="small">
          {COUNTRY_GROUPS.map((g) => (
            <s-button
              key={g.id}
              type="button"
              variant="tertiary"
              disabled={disabled}
              onClick={() => addGroup(g.codes)}
            >
              {g.label}
            </s-button>
          ))}
        </s-stack>
      </s-stack>

      {query.trim() ? (
        <s-box padding="base" borderWidth="base" borderRadius="base">
          {matches.length === 0 ? (
            <s-paragraph color="subdued">
              No countries match “{query.trim()}”.
            </s-paragraph>
          ) : (
            <s-stack direction="block" gap="small">
              {matches.map((c) => (
                <s-button
                  key={c.code}
                  type="button"
                  variant="tertiary"
                  disabled={disabled || !!usedElsewhere[c.code]}
                  onClick={() => add(c.code)}
                >
                  {optionLabel(c)}
                </s-button>
              ))}
            </s-stack>
          )}
        </s-box>
      ) : (
        <s-stack direction="block" gap="small">
          <s-text color="subdued">Popular</s-text>
          <s-stack direction="inline" gap="small">
            {popular.map((c) => (
              <s-button
                key={c.code}
                type="button"
                variant="tertiary"
                disabled={disabled || !!usedElsewhere[c.code]}
                onClick={() => add(c.code)}
              >
                {optionLabel(c)}
              </s-button>
            ))}
          </s-stack>
          <s-paragraph color="subdued">
            Or start typing to search the full country list.
          </s-paragraph>
        </s-stack>
      )}

      <s-button
        type="button"
        variant="tertiary"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? "Hide advanced" : "Advanced: paste country codes"}
      </s-button>
      {showAdvanced ? (
        <s-stack direction="block" gap="base">
          <s-text-field
            label="Bulk country codes"
            value={bulk}
            multiline={2}
            onChange={(e: Event) => setBulk(fieldValue(e))}
            details="ISO codes only if you already have them - e.g. GB, IE, DE"
          />
          <s-button type="button" variant="secondary" onClick={applyBulk}>
            Add codes
          </s-button>
        </s-stack>
      ) : null}
    </s-stack>
  );
}
